import { Dispatch } from 'react';
import { useOutletContext } from 'react-router-dom';
import { FindLocationOrServiceForm } from '../FindLocationOrService/schemas';
import { TicketDetails } from '../TicketDetails/schemas';
import { DeepPartial } from 'react-hook-form';
import { BulkLocations } from '../FindLocationOrService/BulkAddLocations/schemas';
import { Configurations } from 'api/nocexpress/schemas/ConfigurationsSchema';

type WizardReducerStateCommon = {
  details?: {
    local_contact_name?: string;
    local_contact_number?: string;
    maintenance_window?: string;
    access_hours?: string;
    technician_dispatch_approved?: boolean;
    canTestingBeDoneAnytime?: boolean;
  };
  findLocationOrServiceForm?: FindLocationOrServiceForm;
  ticketDetails?: DeepPartial<TicketDetails>;
  services?: Configurations[];
  bulkLocations?: BulkLocations['locations'];
  serviceOptions?: { label: string; value: string }[];
};

export type WizardReducerState =
  | ({
      state: 'FindLocationOrService';
      step: 1;
    } & WizardReducerStateCommon)
  | ({
      state: 'TicketDetails';
      step: 2;
    } & WizardReducerStateCommon);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getWizardInitialState = (initialData?: any) =>
  ({
    state: initialData?.state ?? 'FindLocationOrService',
    step: initialData?.step ?? 1,
    findLocationOrServiceForm:
      initialData?.findLocationOrServiceForm ?? undefined,
    ticketDetails: initialData?.ticketDetails ?? undefined,
    services: initialData?.services ?? undefined,
    partialTicketDetails: undefined,
    bulkLocations: initialData?.bulkLocations ?? undefined,
    serviceOptions: initialData?.serviceOptions ?? undefined,
    details: initialData?.details ?? undefined,
  }) as const;

export type WizardAction =
  | {
      type: 'GoToFindLocationOrService';
      findLocationOrServiceForm?: FindLocationOrServiceForm;
      partialTicketDetails?: DeepPartial<TicketDetails>;
      bulkLocations?: BulkLocations['locations'];
      serviceOptions?: { label: string; value: string }[];
    }
  | {
      type: 'GoToTicketDetails';
      findLocationOrServiceForm?: FindLocationOrServiceForm;
      ticketDetails?: TicketDetails;
      services?: Configurations[];
      bulkLocations?: BulkLocations['locations'];
      partialTicketDetails?: DeepPartial<TicketDetails>;
      serviceOptions?: { label: string; value: string }[];
    }
  | {
      type: 'SaveDetailsPartial';
      partialTicketDetails: DeepPartial<TicketDetails>;
    }
  | {
      type: 'ResubmitWithSelectedIdentifier';
      findLocationOrServiceForm?: DeepPartial<FindLocationOrServiceForm>;
      ticketDetails?: DeepPartial<TicketDetails>;
    }
  | {
      type: 'ResubmitWithPriorTicketAndInitialDescription';
      ticketDetails?: DeepPartial<TicketDetails>;
      findLocationOrServiceForm?: DeepPartial<FindLocationOrServiceForm>;
    }
  | {
      type: 'ResubmitWithPriorTicketAndAddress';
      ticketDetails?: DeepPartial<TicketDetails>;
      findLocationOrServiceForm?: DeepPartial<FindLocationOrServiceForm>;
    };

export const wizardReducer = (
  state: WizardReducerState,
  action: WizardAction,
): WizardReducerState => {
  switch (action.type) {
    case 'ResubmitWithSelectedIdentifier':
      if (!action.findLocationOrServiceForm) {
        throw new Error('Form data must be provided for resubmission');
      }
      return {
        ...getWizardInitialState(),
        findLocationOrServiceForm: {
          ...action.findLocationOrServiceForm,
          //@ts-expect-error workaround
          type: 'site',
        },
        ticketDetails: {
          prior_ticket: action?.ticketDetails?.prior_ticket,
          initialDescription: action?.ticketDetails?.initialDescription,
        },
      };
    case 'ResubmitWithPriorTicketAndAddress':
      return {
        ...getWizardInitialState(),
        findLocationOrServiceForm: {
          ...action.findLocationOrServiceForm,
          //@ts-expect-error workaround
          type: 'site',
        },
        ticketDetails: {
          prior_ticket: action?.ticketDetails?.prior_ticket,
        },
      };
    case 'ResubmitWithPriorTicketAndInitialDescription':
      return {
        ...getWizardInitialState(),
        ticketDetails: {
          prior_ticket: action?.ticketDetails?.prior_ticket,
          initialDescription: action?.ticketDetails?.initialDescription,
        },
      };
    case 'GoToFindLocationOrService':
      return {
        ...getWizardInitialState(),
        findLocationOrServiceForm: action.findLocationOrServiceForm,
        ticketDetails: state.ticketDetails,
        bulkLocations: state.bulkLocations,
        serviceOptions: state.serviceOptions,
      };
    case 'GoToTicketDetails':
      return {
        ...state,
        state: 'TicketDetails',
        findLocationOrServiceForm: action.findLocationOrServiceForm,
        services: action.services,
        ticketDetails: state.ticketDetails,
        step: 2,
        bulkLocations: action.bulkLocations,
        serviceOptions: action.serviceOptions,
      };

    case 'SaveDetailsPartial':
      if (state.state !== 'TicketDetails')
        throw new Error(
          'SaveDetailsPartial can only be called from the TicketDetails page',
        );
      return {
        ...state,
        ticketDetails: action.partialTicketDetails,
      };
  }
};

export type OpenNetOpsTicketWizardContext = {
  dispatch: Dispatch<WizardAction>;
  state: WizardReducerState;
};

export const useOpenNetOpsTicketContext = () => {
  return useOutletContext<OpenNetOpsTicketWizardContext>();
};
